import { translate, userDataStore } from '@/App'
import ShareBar from '@/features/Video/ShareBar/ShareBar'
import Button from '@/shared/components/Button/Button'
import Spinner from '@/shared/components/Spinner/Spinner'
import {
  apiUrl,
  audioClipsUploadsPath,
  audioDescriptionFeedbacks,
} from '@/shared/config'
import ourFetch from '@/shared/utils/ourFetch'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Id, toast } from 'react-toastify'
import YouTube from 'react-youtube'
import { Options, YouTubePlayer } from 'youtube-player/dist/types'
import './video.scss'
import { Howl } from 'howler'
import {
  Clip,
  convertClassicClipObject,
} from '@/shared/utils/convertClipObject'
import convertISO8601ToSeconds from '@/shared/utils/convertISO8601ToSeconds'
import convertViewsToCardFormat from '@/shared/utils/convertViewsToCardFormat'
import VideoPlayerControls from '@/shared/components/VideoPlayerControls/VideoPlayerControls'
import YTInfoCard from '@/features/Video/YTInfoCard/YTInfoCard'
import { convertLikesToCardFormat } from '@/shared/utils/convertLikesToCardFormat'
import { convertISO8601ToDate } from '@/shared/utils/convertISO8601ToDate'
import DescriberCard from '@/features/Video/DescriberCard/DescriberCard'
import RatingPopup from '@/features/Video/RatingPopup/RatingPopup'
import { useRatingPopup } from '@/features/Video/RatingPopup/useRatingPopup'
// Optional feedback UI disabled — see commented-out usages below. Data field (`feedback`) is still sent/stored.
// import FeedbackPopup from '@/features/Video/FeedbackPopup/FeedbackPopup'
import RatingsInfoCard from '@/features/Video/RatingsInfoCard/RatingsInfoCard'
import { ProgressBar } from 'react-bootstrap'
import axios from 'axios'
import { Feedbacks, User, VideoDescriberRoot } from './video_describer'
import LanguageSelector from './LanguageSelector'
import YouTubeService from '@/shared/utils/YouTubeService'
import { TUTORIAL_TARGETS } from '@/features/Tutorial/tutorialSelectors'
import { useTutorialVideoAdapter } from '@/features/Tutorial/useTutorialVideoAdapter'
import { useAudioDescriptionEngine } from '../../shared/hooks/useAudioDescriptionEngine' // <-- Add import

interface IADUserId {
  [key: string]: {
    overall_rating_votes_counter: number
    overall_rating_average: number
    overall_rating_votes_sum: number
    user: User
    feedbacks: Feedbacks
    picture: string
    name: string
    collaborative_edit: boolean
    contributions: Map<string, number>
    displayContributions?: { [key: string]: number }
    prev_audio_description: string
    depth: number
  }
}

interface ApiError {
  response?: {
    status: number
    data: any
  }
  request?: any
  message?: string
}

interface VideoProps {
  isTutorialMode?: boolean
}

const Video = ({ isTutorialMode = false }: VideoProps) => {
  const {
    videoId,
    isBlockedTutorialVideo,
    initialVideoState,
    tutorialDocumentTitle,
  } = useTutorialVideoAdapter(isTutorialMode)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedADId, setSelectedADId] = useState<string>('')
  const isSignedIn = userDataStore((state) => state.isSignedIn)

  const [describerCards, setDescriberCards] = useState<ReactNode[]>([])
  const [descriptionsActive, setDescriptionsActive] = useState(true)
  const [rating, setRating] = useState<number>(0)
  const [enjoymentRating, setEnjoymentRating] = useState<number>(0)
  const [ratingComment, setRatingComment] = useState<string>('')
  const {
    isRatingPopupOpen,
    openRatingPopup,
    closeRatingPopup,
    completeRatingPopup,
    ratingSubmitError,
    setRatingSubmitError,
    ratingSuccessMessage,
  } = useRatingPopup()
  // const codes = iso6391.getAllCodes()

  const languages = [
    { code: 'en-US', name: 'English (United States)' },
    // { code: 'en-GB', name: 'English (United Kingdom)' },
    // { code: 'zh-CN', name: 'Chinese (Simplified, China)' },
    // { code: 'zh-TW', name: 'Chinese (Traditional, Taiwan)' },
    // { code: 'ko-KR', name: 'Korean (South Korea)' },
    // { code: 'fr-FR', name: 'French (France)' },
    // { code: 'fr-CA', name: 'French (Canada)' },
    // { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
    // { code: 'ar-EG', name: 'Arabic (Egypt)' },
    // { code: 'ru-RU', name: 'Russian (Russia)' },
    // { code: 'de-DE', name: 'German (Germany)' },
    // { code: 'es-ES', name: 'Spanish (Spain)' },
    // { code: 'es-MX', name: 'Spanish (Mexico)' },
  ]

  // Loading Spinner
  const [showSpinner, setShowSpinner] = useState(initialVideoState.showSpinner)

  // Data from API
  const [audioDescriptionsIds, setAudioDescriptionsIds] = useState<any[]>([])
  const [audioDescriptionsIdsUsers, setAudioDescriptionsIdsUsers] =
    useState<IADUserId | null>(null)
  const [audioDescriptionsIdsAudioClips, setAudioDescriptionsIdsAudioClips] =
    useState<any>({})

  // YouTube Video Info
  const [videoTitle, setVideoTitle] = useState(initialVideoState.title)
  const [videoAuthor, setVideoAuthor] = useState(initialVideoState.author)
  const [videoPublishedAt, setVideoPublishedAt] = useState(
    initialVideoState.publishedAt,
  )
  const [, setVideoDescription] = useState('')
  const [videoViews, setVideoViews] = useState(initialVideoState.views)
  const [videoLikes, setVideoLikes] = useState(initialVideoState.likes)
  const [videoDurationInSeconds, setVideoDurationInSeconds] = useState(
    initialVideoState.durationSeconds,
  )

  const [sortedAudioClips, setSortedAudioClips] = useState<Clip[]>([])
  const [lastProcessedIndex, setLastProcessedIndex] = useState(-1)

  // Balancer value for volume controls
  const [descriptionVolume, setDescriptionVolume] = useState(
    parseInt(localStorage.getItem('descriptionVolume') || '50'),
  )
  const [youTubeVolume, setYouTubeVolume] = useState(
    parseInt(localStorage.getItem('youTubeVolume') || '100'),
  )
  const descriptionVolumeRef = useRef(descriptionVolume)
  const youTubeVolumeRef = useRef(youTubeVolume)
  const historyTracked = useRef(false)

  //
  // YDX STATE VARIABLES
  //
  const [audioClips, setAudioClips] = useState<Clip[]>([]) // stores list of Audio Clips data for a video from backend db
  const [currentEvent, setCurrentEvent] = useState<YouTubePlayer>() //stores YouTube video's event
  const [currentState, setCurrentState] = useState(-1) // stores YouTube video's PLAYING, CUED, PAUSED, UNSTARTED, BUFFERING, ENDED state values

  const [isActive, setIsActive] = useState(false)
  const [samplingRate] = useState(200)

  const [showLanguageSelector, setShowLanguageSelector] = useState(false)

  const currentEventRef = useRef(currentEvent)
  const currentStateRef = useRef(currentState)

  // 1. Initialize new engine
  const {
    playedClips,
    handleBuffering,
    currentTimeUI,
    stopAllAudio,
    pauseCurrentAudio,
    resumeCurrentAudio,
  } = useAudioDescriptionEngine(
    audioClips,
    currentEventRef.current,
    descriptionVolume,
    descriptionsActive && currentState === 1,
  )

  // Update getPlaybackStats to use the new hook variables
  const getPlaybackStats = () => {
    return {
      totalClips: audioClips.length,
      playedClips: playedClips.size,
      currentTime: currentTimeUI,
    }
  }

  const [requestAiDescription, setRequestAiDescription] = useState<{
    status: string
    requested: boolean
    url?: string
    aiDescriptionId?: string
    preview?: boolean
  }>({
    status: 'notavailable',
    requested: false,
  })

  const [, setButtonLoading] = useState(false)
  const toastId = React.useRef<null | Id>(null)

  const [isAiRequestPending, setIsAiRequestPending] = useState(false)
  const [aiServiceStatus, setAiServiceStatus] = useState<
    'available' | 'unavailable' | 'unknown'
  >('unknown')

  useEffect(() => {
    if (isTutorialMode) {
      setAiServiceStatus('available')
      document.title = tutorialDocumentTitle
      return
    }

    const checkAiService = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/ai-service-status`,
          { withCredentials: true },
        )
        setAiServiceStatus(
          response.data.available ? 'available' : 'unavailable',
        )
      } catch (error) {
        setAiServiceStatus('unavailable')
      }
    }

    checkAiService()
  }, [isTutorialMode, tutorialDocumentTitle])

  useEffect(() => {
    currentEventRef.current = currentEvent
    currentEventRef.current?.setVolume(youTubeVolume)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent])

  useEffect(() => {
    currentStateRef.current = currentState
  }, [currentState])

  useEffect(() => {
    descriptionVolumeRef.current = descriptionVolume
    localStorage.setItem('descriptionVolume', descriptionVolume.toString())
  }, [descriptionVolume])

  useEffect(() => {
    const player = currentEventRef.current
    if (player) {
      if (youTubeVolume === 0) {
        player.mute?.()
      } else {
        player.unMute?.()
        player.setVolume?.(youTubeVolume)
      }
    }
    youTubeVolumeRef.current = youTubeVolume
    localStorage.setItem('youTubeVolume', youTubeVolume.toString())
  }, [youTubeVolume])

  // The YouTube iframe API doesn't emit volume/mute events, so poll the
  // player and mirror external changes (e.g. clicking YouTube's built-in
  // mute button) back into our slider state.
  useEffect(() => {
    if (!currentEvent) return
    let cancelled = false
    const id = setInterval(async () => {
      try {
        const [muted, vol] = await Promise.all([
          currentEvent.isMuted?.() ?? Promise.resolve(false),
          currentEvent.getVolume?.() ?? Promise.resolve(100),
        ])
        if (cancelled) return
        const effective = muted ? 0 : Math.round(vol)
        if (effective !== youTubeVolumeRef.current) {
          setYouTubeVolume(effective)
        }
      } catch {
        // player not ready yet
      }
    }, 500)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [currentEvent])

  useEffect(() => {
    return () => {
      // Ensure all Howl instances are unloaded when audioClips change
      audioClips.forEach((clip) => {
        if (clip.clip_audio) {
          clip.clip_audio.unload()
        }
      })
    }
  }, [audioClips])

  useEffect(() => {
    if (currentState !== 1 || audioClips.length === 0) return

    // Find any clip starting in the next 5 seconds and tell the browser to download it
    const futureThreshold = currentTimeUI + 5
    audioClips.forEach((clip) => {
      if (
        clip.clip_start_time > currentTimeUI &&
        clip.clip_start_time < futureThreshold
      ) {
        if (!clip.clip_audio) {
          clip.clip_audio = new Howl({
            src: clip.clip_audio_path,
            html5: true,
            preload: true,
          })
        }
      }
    })
  }, [currentTimeUI, currentState, audioClips])

  //
  // END OF YDX STATE VARIABLES
  //

  // YouTube Player Options
  const opts: Options = {
    host: 'https://www.youtube-nocookie.com',
    width: '100%',
    height: '400',
    playerVars: {
      autoplay: 0,
      enablejsapi: 1,
      cc_load_policy: 1,
      controls: isTutorialMode ? 0 : 1,
      fs: 0,
      iv_load_policy: 3,
      modestbranding: 1,
      disablekb: isTutorialMode ? 1 : 0,
      rel: 0,
      origin: window.location.origin,
    },
  }

  // Fetch Data on Page Load
  useEffect(() => {
    // console.log(videoId)
    if (!isTutorialMode && !isBlockedTutorialVideo && videoId) {
      fetchVideoData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialMode, isBlockedTutorialVideo])

  useEffect(() => {
    if (
      videoId &&
      videoTitle &&
      !isTutorialMode &&
      !isBlockedTutorialVideo &&
      !historyTracked.current &&
      userDataStore.getState().isSignedIn
    ) {
      saveVideoToHistory(videoId)
      historyTracked.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, videoTitle, isTutorialMode, isBlockedTutorialVideo])

  useEffect(() => {
    if (isTutorialMode || isBlockedTutorialVideo) return

    if (isSignedIn) {
      const url = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/ai-description-status`

      axios
        .post<{
          status: string
          requested: boolean
        }>(
          url,
          {
            youtube_id: videoId,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: userDataStore.getState().userId,
            },
          },
        )
        .then((response) => {
          const data = response.data
          setRequestAiDescription(data)
        })
        .catch((error) => {
          if (error.response && error.response.status === 500) {
            // Handle the 500 Internal Server Error here
            const errorMessage =
              'Internal Server Error: Something went wrong on the server side.Please try again later! '
            toast.error(errorMessage)
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isTutorialMode, isBlockedTutorialVideo])

  const fetchVideoData = () => {
    const url = `${apiUrl}/videos/${videoId}`
    ourFetch(url)
      .then((res) => {
        parseVideoData(res.result)
      })
      .catch((err) => {
        console.log(err)
        // navigate('/not-found')
      })
  }

  const checkPlaybackTypeBeforePlaying = async (clip: Clip): Promise<Clip> => {
    console.log(
      'Checking playback type for clip:',
      clip.clip_id,
      'current type:',
      clip.playback_type,
    )
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_YDX_BACKEND_URL}/api/audio-clips/get-playback-type/${clip.clip_id}`,
        { withCredentials: true },
      )

      console.log('API response for playback type:', response.data)

      if (response.data.playback_type !== clip.playback_type) {
        console.info(
          `Playback type changed for clip ${clip.clip_id}: ${clip.playback_type} -> ${response.data.playback_type}`,
        )

        // Update the clip's playback type
        clip.playback_type = response.data.playback_type

        // Also update the clip in the audio clips array to ensure consistency
        const updatedAudioClips = [...audioClips]
        const clipIndex = updatedAudioClips.findIndex(
          (c) => c.clip_id === clip.clip_id,
        )
        if (clipIndex !== -1) {
          updatedAudioClips[clipIndex].playback_type =
            response.data.playback_type
          setAudioClips(updatedAudioClips)
        }
      }

      console.log('Returning clip with playback type:', clip.playback_type)
      return clip
    } catch (error) {
      console.error('Error fetching current playback type:', error)
      return clip
    }
  }

  const parseVideoData = (videoData: VideoDescriberRoot) => {
    const adIds: string[] = []
    const adIdsUsers: IADUserId = {}
    const adIdsAudioClips: { [key: string]: any[] } = {}

    if (
      videoData.audio_descriptions &&
      videoData.audio_descriptions.length > 0
    ) {
      videoData.audio_descriptions.forEach((ad) => {
        if (ad.status !== 'published' && ad.user?.user_type !== 'AI') {
          console.log('Skipping draft audio description:', ad._id)
          return // Skip this iteration
        }

        adIds.push(ad._id)

        // Initialize adIdsUsers[ad._id] as an object if it doesn't exist
        if (!adIdsUsers[ad._id]) {
          adIdsUsers[ad._id] = {
            overall_rating_votes_counter: ad.overall_rating_votes_counter,
            overall_rating_average: ad.overall_rating_votes_average,
            overall_rating_votes_sum: ad.overall_rating_votes_sum,
            feedbacks: ad.feedbacks,
            picture: ad.user.picture,
            user: ad.user,

            name:
              ad.user?.user_type === 'AI'
                ? 'AI Description Draft'
                : ad.user?.name || 'Unknown',
            collaborative_edit: ad.collaborative_editing,
            contributions: ad.contributions,
            displayContributions: ad.displayContributions,
            prev_audio_description: ad.prev_audio_description,
            depth: ad.depth,
          }
        } else {
          adIdsUsers[ad._id].name =
            ad.user?.user_type === 'AI'
              ? 'AI Description Draft'
              : ad.user?.name || 'Unknown'
        }

        // Initialize adIdsAudioClips[adId]
        adIdsAudioClips[ad._id] = []

        if (Array.isArray(ad.audio_clips) && ad.audio_clips.length > 0) {
          ad.audio_clips.forEach((audioClip, clipIndex) => {
            if (!audioClip || typeof audioClip !== 'object') {
              return // Skip this audio clip
            }

            if (!audioClip.file_path || !audioClip.file_name) {
              return // Skip this audio clip
            }

            const filePath = audioClip.file_path.replace(/^\./, '')
            const clipUrl = `${audioClipsUploadsPath(
              `${filePath}/${audioClip.file_name}`,
            )}`

            adIdsAudioClips[ad._id].push({
              ...audioClip,
              url: clipUrl,
            })
          })
        } else {
          console.log(`No audio clips found for adId: ${ad._id}`)
        }
      })

      setAudioDescriptionsIds(adIds)
      setAudioDescriptionsIdsUsers(adIdsUsers)
      setAudioDescriptionsIdsAudioClips(adIdsAudioClips)
      setAudioDescriptionActive(adIdsUsers, adIdsAudioClips)
    } else {
      console.log('No audio descriptions available')
      getYTVideoInfo()
    }
  }

  const getHighestRatedAudioDescription = (adIdsUsers: any) => {
    let maxAvarage = 0
    let selectedId = null
    Object.keys(adIdsUsers).forEach((adId, idx) => {
      const current = adIdsUsers[adId]
      if (idx === 0) {
        selectedId = adId
        if (current.overall_rating_average) {
          maxAvarage = current.overall_rating_average
        }
      } else if (current.overall_rating_average > maxAvarage) {
        selectedId = adId
        maxAvarage = current.overall_rating_average
      }
    })
    return selectedId
  }

  const setAudioDescriptionActive = (adIdsUsers: any, adIdsAudioClips: any) => {
    let selectedAd = searchParams.get('ad')
    if (!selectedAd) {
      selectedAd = getHighestRatedAudioDescription(adIdsUsers)
    }
    // console.log('Selected AD', selectedAd)

    if (!selectedAd || !adIdsAudioClips[selectedAd]) {
      console.log('No valid audio description selected')
      getYTVideoInfo()
      return
    }

    if (
      audioDescriptionsIds?.length &&
      audioDescriptionsIds?.indexOf(selectedAd) === -1
    ) {
      // console.log('Navigating to Not Found')
      // navigate('/not-found')
    }
    setSearchParams(
      (params) => {
        if (selectedAd) params.set('ad', selectedAd)
        return params
      },
      { replace: true },
    )
    setSelectedADId(selectedAd ?? '')
    prepareAudioClips(selectedAd ?? '', adIdsAudioClips)
  }

  const prepareAudioClips = (selectedAdId: string, adIdsAudioClips: any) => {
    const selectedAudioClips = adIdsAudioClips[selectedAdId]

    // 1. URL Testing for extended clips (Keep this, it's good for debugging!)
    const extendedClip = selectedAudioClips.find(
      (clip: { playback_type: string }) => clip.playback_type === 'extended',
    )
    if (extendedClip) {
      const testUrl = `${audioClipsUploadsPath(
        `${extendedClip.file_path.replace(/^\./, '')}/${
          extendedClip.file_name
        }`,
      )}`
      console.log('Testing extended clip URL:', testUrl)

      fetch(testUrl)
        .then((response) => {
          console.log(
            'Extended clip URL test response:',
            response.status,
            response.statusText,
          )
        })
        .catch((error) => {
          console.error('Extended clip URL test failed:', error)
        })
    }

    // 2. Format and Sort the clips
    const audioClipsData: Clip[] = selectedAudioClips.map(
      (audioClip: any, index: number) => {
        const clip = convertClassicClipObject(audioClip)
        clip.clip_sequence_number = index + 1
        return clip
      },
    )

    const sortedClipData = audioClipsData.sort(
      (a, b) => a.clip_start_time - b.clip_start_time,
    )

    // 3. Set the master array (The custom hook will read this!)
    setSortedAudioClips(sortedClipData)
    setAudioClips([...sortedClipData])

    // 4. Fetch YouTube info
    getYTVideoInfo()
  }

  const getYTVideoInfo = () => {
    // Early check for videoId
    if (!videoId) {
      console.error('Video ID is undefined')
      setShowSpinner(false)
      return
    }

    YouTubeService.getVideoDetails(videoId)
      .then((videoDetails) => {
        if (!videoDetails || videoDetails.length === 0) {
          alert('Video Unavailable!')
          setShowSpinner(false)
          return
        }

        const videoData = videoDetails[0]

        // Use optional chaining with defaults to avoid TypeScript errors
        const videoDurationInSeconds = videoData?.contentDetails?.duration
          ? convertISO8601ToSeconds(videoData.contentDetails.duration)
          : 0

        setVideoDurationInSeconds(videoDurationInSeconds)

        // Handle all other properties safely with optional chaining
        if (videoData?.snippet?.title) {
          setVideoTitle(videoData.snippet.title)
          document.title = `YouDescribe - ${videoData.snippet.title}`
        }

        if (videoData?.snippet?.channelTitle) {
          setVideoAuthor(videoData.snippet.channelTitle)
        }

        if (videoData?.snippet?.publishedAt) {
          setVideoPublishedAt(
            convertISO8601ToDate(videoData.snippet.publishedAt),
          )
        }

        if (videoData?.statistics?.likeCount) {
          setVideoLikes(
            convertLikesToCardFormat(Number(videoData.statistics.likeCount)),
          )
        }

        if (videoData?.snippet?.description) {
          setVideoDescription(videoData.snippet.description)
        }

        if (videoData?.statistics?.viewCount) {
          setVideoViews(
            convertViewsToCardFormat(Number(videoData.statistics.viewCount)),
          )
        }

        setShowSpinner(false)
      })
      .catch((err) => {
        console.error('Unable to load the video:', err)
        toast.error(
          'Thank you for visiting YouDescribe. This video is not viewable at this time.',
        )
        setShowSpinner(false)
      })
  }
  useEffect(() => {
    return () => {
      // If component unmounts before history is saved, try to save it
      if (
        !isTutorialMode &&
        !historyTracked.current &&
        videoId &&
        userDataStore.getState().isSignedIn
      ) {
        saveVideoToHistory(videoId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, isTutorialMode])

  //
  //
  // REFACTORED YDX FUNCTIONS (Hook Integration)
  //
  //

  const saveVideoToHistory = async (
    videoId: string,
    retryCount = 0,
  ): Promise<boolean> => {
    if (isTutorialMode) return false
    if (!userDataStore.getState().isSignedIn || !videoId) return false

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/save-Visited-Videos-History`,
        {
          youtube_id: videoId,
          invalidate_cache: true,
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      return response.status === 201
    } catch (error) {
      console.error('Error saving video history:', error)
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000
        setTimeout(() => {
          saveVideoToHistory(videoId, retryCount + 1)
        }, delay)
      }
      return false
    }
  }

  // YouTube Player Functions
  const onReady = (event: any) => {
    setCurrentEvent(event.target)

    if (isTutorialMode) {
      event.target.pauseVideo()
      event.target.getIframe?.().then((iframe: HTMLIFrameElement) => {
        iframe.setAttribute('aria-hidden', 'true')
        iframe.setAttribute('tabindex', '-1')
        iframe.setAttribute('title', 'Tutorial video playback disabled')
      })
    }
  }

  const onPlay = (event: any) => {
    setCurrentEvent(event.target)
    if (isTutorialMode) {
      event.target.pauseVideo()
      setCurrentState(2)
      setIsActive(false)
      return
    }

    if (!isTutorialMode && !historyTracked.current && videoId) {
      saveVideoToHistory(videoId)
      historyTracked.current = true
    }
  }

  const onPause = (event: any) => {
    event.target.pauseVideo()
  }

  const onStateChange = (event: any) => {
    const currentYTTime = event.target.getCurrentTime()
    setCurrentEvent(event.target)

    if (isTutorialMode) {
      if (event.data === 1) {
        event.target.pauseVideo()
      }
      setCurrentState(event.data === 1 ? 2 : event.data)
      setIsActive(false)
      return
    }

    setCurrentState(event.data)

    switch (event.data) {
      case 1: // Playing
        if (!isActive) setIsActive(true)
        resumeCurrentAudio()
        break
      case 2: // Paused
        pauseCurrentAudio()
        break
      case 3: // Buffering — resets the engine only if the playhead jumped
        handleBuffering(currentYTTime)
        break
      case 0: // Ended
        setIsActive(false)
        break
    }
  }

  useEffect(() => {
    if (audioDescriptionsIdsUsers) {
      // console.log('Updating describer Cards')
      const describers = audioDescriptionsIdsUsers
      const describerCards: ReactNode[] = []
      let describerIds = Object.keys(describers)

      if (describerIds.length) {
        // document.getElementById('no-descriptions').style.display = 'none'
      }
      if (describerIds.length && describerIds[0] !== selectedADId) {
        const selectedIdIndex = describerIds.indexOf(selectedADId)
        describerIds = describerIds
          .splice(selectedIdIndex, 1)
          .concat(describerIds)
      }

      if (!isTutorialMode && videoId && !showSpinner && videoTitle) {
        saveVideoToHistory(videoId)
      }

      describerIds.forEach((describerId, i) => {
        describerCards.push(
          <DescriberCard
            key={i}
            handleDescriberChange={handleDescriberChange}
            handleRatingPopup={handleRatingPopup}
            // handleFeedbackPopup={handleFeedbackPopup}
            handleNewCollabEdit={() => handleNewCollabEdit(selectedADId)}
            describerId={describerId}
            describerUserId={describers[describerId].user?._id}
            selectedDescriberId={selectedADId}
            picture={describers[describerId].picture}
            name={describers[describerId].name}
            overall_rating_average={
              describers[describerId].overall_rating_average
            }
            handleRating={() => {
              // console.log('Handle Rating')
            }}
            videoId={videoId}
            collaborativeEdit={
              (describers[describerId].user?.user_type === 'AI' ||
                describers[describerId].collaborative_edit) &&
              (!describers[describerId].depth ||
                describers[describerId].depth < 3) &&
              checkUserCanCollaborate(describers, describerId)
            }
            contributions={describers[describerId].contributions}
            displayContributions={describers[describerId].displayContributions}
          />,
        )
      })

      setDescriberCards(describerCards)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audioDescriptionsIdsUsers,
    selectedADId,
    videoId,
    showSpinner,
    videoTitle,
    isTutorialMode,
  ])

  const checkUserCanCollaborate = (
    ads: IADUserId | null,
    selectedDescriberId: string,
  ) => {
    if (!ads) return false

    const userId = userDataStore.getState().userId
    const selectedId = selectedDescriberId

    for (const describerId of Object.keys(ads)) {
      const adUserId = ads[describerId].user._id
      const prevAdId = ads[describerId].prev_audio_description

      if (adUserId === userId && prevAdId === selectedId) {
        return false
      }
    }

    return true
  }

  const upVote = () => {
    if (isTutorialMode) {
      return
    }

    if (!userDataStore.getState().isSignedIn) {
      toast.error(translate('You have to be logged in in order to vote'))
    } else {
      const url = `${apiUrl}/wishlist/add-one-wishlist-item`
      ourFetch(url, true, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          youTubeId: videoId,
          userId: userDataStore.getState().userId,
          userToken: userDataStore.getState().userToken,
        }),
      })
        .then((res) => {
          toast.success(translate('Success upVote'))
        })
        .catch((err) => {
          switch (err.status) {
            case 400:
              toast.error(translate(err.message))
              break
            case 200:
              toast.success(translate(err.message))
              break
            default:
              toast.error(
                translate(
                  'It was impossible to vote. Maybe your session has expired. Try to logout and login again.',
                ),
              )
          }
        })
    }
  }

  const handleDescriberChange = (describerId: string) => {
    // 1. Tell the custom hook to instantly kill any playing audio
    stopAllAudio()

    // 2. Pause the YouTube video
    currentEventRef.current?.pauseVideo()

    // 3. Update your UI state to load the new describer
    setSelectedADId(describerId)
    setSearchParams((params) => {
      if (describerId) params.set('ad', describerId)
      return params
    })
    setAudioDescriptionActive(
      audioDescriptionsIdsUsers,
      audioDescriptionsIdsAudioClips,
    )
  }

  // 2. Replace your old handleTurnOffDescriptions with this clean version
  const handleTurnOffDescriptions = () => {
    stopAllAudio() // The hook handles killing the Howler objects instantly
    currentEventRef.current?.pauseVideo()
    setDescriptionsActive(false)
  }

  // 3. And for good measure, here is the paired turn-on function
  const handleTurnOnDescriptions = () => {
    if (isTutorialMode) {
      return
    }

    setDescriptionsActive(true)
    currentEventRef.current?.playVideo() // Optional: auto-resume video when turned back on
  }

  const handleRatingSubmit = (
    comprehensionRating: number,
    enjoymentScore: number,
    ratingCommentText: string,
  ) => {
    // The popup validates that both questions are answered and reports it in its
    // own alert region, so this only has to handle session/server failures.
    if (!userDataStore.getState().isSignedIn) {
      setRatingSubmitError(
        translate('You have to be logged in in order to vote'),
      )
    } else {
      const url = `${apiUrl}/audio-descriptions/ratings/addOne/${selectedADId}`
      ourFetch(url, true, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userDataStore.getState().userId,
          userToken: userDataStore.getState().userToken,
          rating: comprehensionRating,
          enjoymentRating: enjoymentScore,
          comment: ratingCommentText.trim(),
        }),
      })
        .then((res) => {
          // Closes the popup (which hands focus back to the trigger) and puts the
          // confirmation in a live region instead of focusing a div that is then
          // torn down a second later.
          completeRatingPopup(translate('Thanks for rating this description!'))

          /* start of email */
          sendOptInEmail(2, comprehensionRating, [])
          /* end of email */

          // }
          // else {
          //   // this.handleFeedbackPopup();
          // }
          // The API returns the recomputed aggregate, so the stars show exactly
          // what the next page load will. Adding to the local copy instead used
          // to double-count: it always incremented the vote counter, even when
          // the user was changing a rating they had already given, which the
          // server correctly treats as a replacement rather than a new vote.
          const { overallRating } = res as unknown as {
            overallRating?: {
              overall_rating_votes_sum: number
              overall_rating_votes_counter: number
              overall_rating_votes_average: number
            }
          }
          const selectedId = selectedADId

          if (overallRating && audioDescriptionsIdsUsers?.[selectedId]) {
            const describers = { ...audioDescriptionsIdsUsers }
            describers[selectedId].overall_rating_votes_sum =
              overallRating.overall_rating_votes_sum
            describers[selectedId].overall_rating_votes_counter =
              overallRating.overall_rating_votes_counter
            describers[selectedId].overall_rating_average =
              overallRating.overall_rating_votes_average

            setAudioDescriptionsIdsUsers(describers)
          }
        })
        .catch((err) => {
          // console.log(err)
          // Keep the popup open so the answers are not lost, and report the
          // failure inside it where the alert region is reachable.
          setRatingSubmitError(
            translate(
              'It was impossible to vote. Maybe your session has expired. Try to logout and login again.',
            ),
          )
        })
    }
  }

  // Optional feedback UI disabled — data field (`feedback`) kept on the rating payload, just never populated from the UI anymore.
  // const handleFeedbackSubmit = (feedback: any) => {
  //   const url = `${apiUrl}/audio-descriptions/ratings/addOne/${selectedADId}`
  //   ourFetch(url, true, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({
  //       userId: userDataStore.getState().userId,
  //       userToken: userDataStore.getState().userToken,
  //       rating: rating,
  //       feedback,
  //     }),
  //   })
  //     .then((res) => {
  //       const feedbackPopup = document.getElementById('feedback-popup')
  //       const feedbackSuccess = document.getElementById('feedback-success')
  //       if (feedbackPopup) {
  //         feedbackPopup.style.display = 'none'
  //       }
  //       if (feedbackSuccess) {
  //         feedbackSuccess.style.display = 'block'
  //         feedbackSuccess.focus()
  //         setTimeout(() => (feedbackSuccess.style.display = 'none'), 1000)
  //       }
  //       // toast.error('Thanks for your feedback!');
  //
  //       /* start of email */
  //       sendOptInEmail(2, rating, feedback)
  //       /* end of email */
  //     })
  //     .catch((err) => {
  //       // console.log(err)
  //       toast.error(
  //         translate(
  //           'It was impossible to vote. Maybe your session has expired. Try to logout and login again.',
  //         ),
  //       )
  //     })
  // }

  const sendOptInEmail = (optIn: number, rating = 0, feedback = []) => {
    let emailBody = ''
    if (optIn == 1) {
      emailBody = `Your audio description for ${videoTitle} has been viewed. 
      View it here:  ${window.location.href}`
    } else if (optIn == 2) {
      emailBody = `Your audio description for  ${videoTitle} has been rated as ${rating}.
      View it here: ${window.location.href}`
      emailBody +=
        feedback.length > 0 ? ', with the following comment(s):' : '.'
      feedback.forEach((index) => {
        emailBody += `\n${audioDescriptionFeedbacks[index]}`
      })
    }

    const url = `${apiUrl}/users/sendoptinemail`
    const optionObj = {
      method: 'POST' as const,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedADId,
        optin: optIn,
        emailbody: emailBody,
      }),
    }
    ourFetch(url, true, optionObj).then((response) => {
      // console.log(response)
    })
  }

  const handleRatingPopup = () => {
    if (!userDataStore.getState().isSignedIn) {
      toast.error(translate('You have to be logged in in order to vote'))
    } else {
      openRatingPopup()
    }
  }
  // const handleFeedbackPopup = () => {
  //   if (!userDataStore.getState().isSignedIn) {
  //     toast.error(
  //       translate('You have to be logged in in order to give feedback'),
  //     )
  //   } else {
  //     const feedbackPopup = document.getElementById('feedback-popup')
  //     if (feedbackPopup) {
  //       feedbackPopup.style.display = 'block'
  //       feedbackPopup.focus()
  //     }
  //   }
  // }

  const handleRatingPopupClose = () => {
    closeRatingPopup()
  }

  // const handleFeedbackPopupClose = () => {
  //   const feedbackPopup = document.getElementById('feedback-popup')
  //   if (feedbackPopup) {
  //     feedbackPopup.style.display = 'none'
  //   }
  // }

  // console.log(videoDurationInSeconds)

  const getAudioSegments = () => {
    return audioClips.map((ad) => {
      return (
        <div
          key={ad.clip_id}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            backgroundColor:
              ad.playback_type === 'extended'
                ? 'var(--extended-color)'
                : 'var(--inline-color)',
            left: `${(ad.clip_start_time / videoDurationInSeconds) * 100}%`,
            width:
              ad.playback_type === 'extended'
                ? `0.5%`
                : `${(ad.clip_duration / videoDurationInSeconds) * 100}%`,
          }}
        />
      )
    })
  }
  const handleNewCollabEdit = async (oldDescriberId: string) => {
    if (isTutorialMode) {
      return
    }

    if (!userDataStore.getState().isSignedIn) {
      toast.error(
        translate('You have to be logged in in order to add a description'),
      )
    } else {
      try {
        const collabUrl = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/create-collaborative-ad`
        const response = await axios.post(
          collabUrl,
          {
            youtubeVideoId: videoId,
            oldDescriberId: oldDescriberId,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        const data = response.data
        console.log('inside handle new collab....')

        navigate(`/editor/${data.url}`)
      } catch (error) {
        console.log(error)
        toast.error('Something went wrong, please try again later')
      }
    }
  }

  const handleAddDescription = async () => {
    if (isTutorialMode) {
      return
    }

    // console.log(userDataStore.getState())
    if (!userDataStore.getState().isSignedIn) {
      toast.error(
        translate('You have to be logged in in order to add a description'),
      )
    } else {
      try {
        const url = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/create-new-user-ad`
        const response = await axios.post(
          url,
          {
            youtubeVideoId: videoId,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        const data = response.data
        // console.log(data)
        navigate(`/editor/${data.url}`)
      } catch (error) {
        // console.log(error)
        toast.error('Something went wrong, please try again later')
      }
    }
  }

  const handleGenerateAIDescriptions = async (languageCode: string) => {
    if (isTutorialMode) {
      return
    }

    if (!userDataStore.getState().isSignedIn) {
      toast.error(
        translate(
          'You have to be logged in in order to ask for AI Descriptions',
        ),
      )
      return
    }

    if (videoDurationInSeconds > 600) {
      toast.error(
        translate(
          'YouDescribe currently supports videos that are 10 minutes or less. Please wait for further updates.',
        ),
      )
      return
    }

    setIsAiRequestPending(true)

    try {
      if (requestAiDescription.status === 'pending') {
        const url = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/increase-Request-Count`
        await axios.post(
          url,
          { youtube_id: videoId },
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          },
        )

        toast.info(
          translate(
            'AI Descriptions are already being generated. You will receive an email when they are ready.',
          ),
        )
        return
      }

      //const url = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/request-ai-descriptions-with-gpu`
      const url = `${process.env.REACT_APP_YDX_BACKEND_URL}/api/users/request-ai-descriptions-with-lana`
      const response = await axios.post(
        url,
        {
          youtube_id: videoId,
          selectedLanguageCode: languageCode,
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        },
      )

      if (response.data) {
        setRequestAiDescription({
          status: 'pending',
          requested: true,
        })
        toast.success(
          'AI Descriptions have been requested successfully. You will receive an email when they are ready.',
        )
      }
    } catch (error: unknown) {
      console.error('AI Description request failed:', error)

      setRequestAiDescription({
        status: 'notavailable',
        requested: false,
      })

      const apiError = error as ApiError

      if (apiError.response) {
        switch (apiError.response.status) {
          case 400:
            toast.error(
              'Invalid request. Please check your input and try again.',
            )
            break
          case 401:
            toast.error('Please log in to request AI descriptions.')
            break
          case 429:
            toast.error('Too many requests. Please try again later.')
            break
          case 500:
            toast.error(
              'The AI description service is currently unavailable. Please try again later.',
            )
            break
          default:
            toast.error('Something went wrong. Please try again later.')
        }
      } else if (apiError.request) {
        toast.error(
          'Network error. Please check your connection and try again.',
        )
      } else {
        toast.error('An unexpected error occurred. Please try again later.')
      }
    } finally {
      setIsAiRequestPending(false)
    }
  }

  const handleRequestAIDescriptions = () => {
    if (isTutorialMode) {
      return
    }

    if (videoDurationInSeconds > 600) {
      toast.error(
        translate(
          'YouDescribe currently supports videos that are 10 minutes or less. Please wait for further updates.',
        ),
      )
      return
    }
    // Show the language selector modal
    setShowLanguageSelector(true)
  }

  // Function to handle the confirmation of language selection
  const handleLanguageConfirm = (selectedLanguageCode: string) => {
    // Generate AI descriptions with the selected language
    handleGenerateAIDescriptions(selectedLanguageCode)
    // Close the language selector modal
    setShowLanguageSelector(false)
  }

  // Function to handle canceling language selection
  const handleLanguageCancel = () => {
    // Close the language selector modal
    setShowLanguageSelector(false)
  }

  const renderDescriptionButtons = () => {
    if (
      requestAiDescription.status === 'completed' &&
      requestAiDescription.url
    ) {
      return (
        <div className="description-buttons">
          <div
            data-tutorial={
              isTutorialMode ? TUTORIAL_TARGETS.freestyleBtn : undefined
            }
          >
            <Button
              title={translate('Add a new description for this video')}
              ariaLabel="Add a new description for this video"
              text={translate('Add Freestyle Description')}
              color="youdescribe-yellow w3-block w3-margin-top"
              onClick={handleAddDescription}
            />
          </div>
        </div>
      )
    }

    if (requestAiDescription.status === 'pending') {
      return (
        <div className="description-buttons">
          <div
            data-tutorial={
              isTutorialMode ? TUTORIAL_TARGETS.freestyleBtn : undefined
            }
          >
            <Button
              title={translate('Add a new description for this video')}
              ariaLabel="Add a new description for this video"
              text={translate('Add Freestyle Description')}
              color="youdescribe-yellow w3-block w3-margin-top"
              onClick={handleAddDescription}
            />
          </div>
          {requestAiDescription.requested ? (
            <Button
              title={translate('AI Descriptions requested')}
              ariaLabel="AI Descriptions requested"
              text={translate('AI Descriptions requested')}
              color="w3-brown w3-block w3-margin-top"
              disabled={true}
            />
          ) : (
            <div
              data-tutorial={
                isTutorialMode ? TUTORIAL_TARGETS.requestAiBtn : undefined
              }
            >
              <Button
                title={translate('Request AI Descriptions')}
                ariaLabel="Request AI Descriptions"
                text={translate('Request AI Descriptions')}
                color="w3-light-blue w3-block w3-margin-top"
                disabled={requestAiDescription.requested}
                onClick={handleRequestAIDescriptions}
              />
            </div>
          )}
          {showLanguageSelector && (
            <LanguageSelector
              show={showLanguageSelector}
              handleClose={handleLanguageCancel}
              handleGenerateAIDescriptions={handleLanguageConfirm}
              languages={languages}
              showLanguageSelector={showLanguageSelector}
            />
          )}
        </div>
      )
    }

    if (
      (requestAiDescription.status === 'notavailable' ||
        requestAiDescription.status === 'draft') &&
      !requestAiDescription.requested
    ) {
      return (
        <div className="description-buttons">
          <div
            data-tutorial={
              isTutorialMode ? TUTORIAL_TARGETS.freestyleBtn : undefined
            }
          >
            <Button
              title={translate('Add a new description for this video')}
              ariaLabel="Add a new description for this video"
              text={translate('Add Freestyle Description')}
              color="youdescribe-yellow w3-block w3-margin-top"
              onClick={handleAddDescription}
              disabled={requestAiDescription.requested}
            />
          </div>
          <div
            data-tutorial={
              isTutorialMode ? TUTORIAL_TARGETS.requestAiBtn : undefined
            }
          >
            <Button
              title={translate('Request AI Descriptions')}
              ariaLabel="Request AI Descriptions"
              text={translate('Request AI Descriptions')}
              color="w3-light-blue w3-block w3-margin-top"
              disabled={requestAiDescription.requested}
              onClick={handleRequestAIDescriptions}
            />
          </div>
          {showLanguageSelector && (
            <LanguageSelector
              show={showLanguageSelector}
              handleClose={handleLanguageCancel}
              handleGenerateAIDescriptions={handleLanguageConfirm}
              languages={languages}
              showLanguageSelector={showLanguageSelector}
            />
          )}
        </div>
      )
    }

    return <></>
  }

  if (isBlockedTutorialVideo) {
    return null
  }

  return (
    <div id="video-page" className="video-page">
      {/* The page had no banner landmark and no h1 at all. The title is already
          shown visually by YTInfoCard, so this is the screen-reader-only
          equivalent rather than a second visible header bar. */}
      <header role="banner" className="visually-hidden">
        <h1>{videoTitle || translate('Video')}</h1>
      </header>
      <main role="main" className="video-page-main" aria-label="Video page">
        <section id="video-area" className="video-area">
          {/* <ToastContainer /> */}
          <ShareBar videoTitle={videoTitle} />
          <div id="video" className="video">
            {showSpinner ? <Spinner /> : null}
            <YouTube
              className="rounded"
              videoId={videoId}
              opts={opts}
              style={isTutorialMode ? { pointerEvents: 'none' } : undefined}
              title={
                isTutorialMode ? 'Tutorial video playback disabled' : undefined
              }
              onStateChange={onStateChange}
              onPlay={onPlay}
              onPause={onPause}
              onReady={onReady}
            />
          </div>
          <div className="classic-container audio-ducking-container">
            <VideoPlayerControls
              descriptionVolume={descriptionVolume}
              setDescriptionVolume={setDescriptionVolume}
              youTubeVideoVolume={youTubeVolume}
              setYouTubeVideoVolume={setYouTubeVolume}
            />
          </div>
          <div className="classic-container video-timeline" aria-hidden="true">
            <ProgressBar
              style={{
                position: 'relative',
                height: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '7px',
                overflow: 'hidden',
              }}
            >
              {getAudioSegments()}
            </ProgressBar>
            <div
              style={{
                position: 'absolute',
                top: 0,
                zIndex: 20,
                height: '28px',
                backgroundColor: 'red',
                left: `${(currentTimeUI / videoDurationInSeconds) * 100}%`,
                width: '0.2%',
              }}
            />
          </div>
        </section>
        <section
          id="video-info"
          className="classic-container w3-row video-info"
        >
          <RatingPopup
            isOpen={isRatingPopupOpen}
            audioDescriptionId={selectedADId}
            comprehensionRating={rating}
            setComprehensionRating={setRating}
            enjoymentRating={enjoymentRating}
            setEnjoymentRating={setEnjoymentRating}
            comment={ratingComment}
            setComment={setRatingComment}
            submitError={ratingSubmitError}
            handleRatingSubmit={handleRatingSubmit}
            handleRatingPopupClose={handleRatingPopupClose}
          />
          {/* Announcement channel: always mounted so the live region is
              registered before it gains text. The visual confirmation below is
              a separate node, hidden from assistive tech to avoid saying it twice. */}
          <div className="visually-hidden" role="status">
            {ratingSuccessMessage}
          </div>
          {ratingSuccessMessage && (
            <div
              id="rating-success"
              className="rating-success"
              aria-hidden="true"
            >
              {ratingSuccessMessage}
            </div>
          )}
          {/* Optional feedback UI disabled — data field (`feedback`) kept on the rating payload.
          <FeedbackPopup
            handleFeedbackSubmit={handleFeedbackSubmit}
            handleFeedbackPopupClose={handleFeedbackPopupClose}
          />
          <div id="feedback-success" className="feedback-success" tabIndex={-1}>
            {translate('Thank you for your feedback!')}
          </div>
          */}
          <div className="w3-col l8 m8">
            <YTInfoCard
              videoTitle={videoTitle}
              videoAuthor={videoAuthor}
              videoViews={videoViews}
              videoPublishedAt={videoPublishedAt}
              videoLikes={videoLikes}
            />
            {searchParams.get('show') && (
              <RatingsInfoCard
                selectedAudioDescriptionId={selectedADId}
                audioDescriptionsIdsUsers={audioDescriptionsIdsUsers}
              />
            )}
          </div>
          {descriptionsActive ? (
            <div
              id="describers"
              className="w3-col l4 m4 describers"
              style={{
                display: Object.keys(audioDescriptionsIdsUsers || {}).length
                  ? 'block'
                  : 'none',
              }}
            >
              <div className="w3-card-2">
                <h3 className="classic-h3">
                  {translate('Selected description')}
                </h3>
                {describerCards[0]}
                <hr aria-hidden="true" />
                <h3 className="classic-h3">
                  {translate('Other description options')}
                </h3>
                {describerCards.slice(1)}
                <Button
                  title={translate('Turn off descriptions for this video')}
                  text={translate('Turn off descriptions')}
                  color="w3-indigo w3-block w3-margin-top"
                  ariaLabel="Turn off descriptions for this video"
                  onClick={handleTurnOffDescriptions}
                />
                {renderDescriptionButtons()}
              </div>
            </div>
          ) : (
            <div
              id="descriptions-off"
              className="w3-col l4 m4 descriptions-off"
            >
              <div className="w3-card-2">
                <h3 className="classic-h3">{translate('Descriptions off')}</h3>
                <Button
                  title={translate('Turn on descriptions for this video')}
                  ariaLabel="Turn on descriptions for this video"
                  text={translate('Turn on descriptions')}
                  color="w3-indigo w3-block w3-margin-top"
                  onClick={handleTurnOnDescriptions}
                />
              </div>
            </div>
          )}
          <div
            id="no-descriptions"
            className="w3-col l4 m4"
            style={{
              display: Object.keys(audioDescriptionsIdsUsers || {}).length
                ? 'none'
                : 'block',
            }}
          >
            <div className="w3-card-2">
              {requestAiDescription.status === 'available' ? (
                <h3 className="classic-h3">AI descriptions available</h3>
              ) : (
                <h3 className="classic-h3">No descriptions available</h3>
              )}
              <div
                data-tutorial={
                  isTutorialMode ? TUTORIAL_TARGETS.wishlistBtn : undefined
                }
              >
                <Button
                  title={translate(
                    'Request an audio description for this video',
                  )}
                  ariaLabel="Request an audio description for this video"
                  text={translate('Add to WISHLIST')}
                  color="w3-indigo w3-block w3-margin-top"
                  onClick={() => upVote()}
                />
              </div>
              {renderDescriptionButtons()}
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default Video
